import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Activity, BarChart3, Users, Trophy } from "lucide-react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import ErrorBoundary from "../../../../components/shared/error/ErrorBoundary";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import MobileNCAAFGamesView from "../components/mobile/MobileNCAAFGamesView";
import TabNavigation from "../components/mobile/TabNavigation";
import RailNavigation from "../../../../components/dashboards/mobile/RailNavigation";
import { TeamSheet, ConferenceSheet } from "../../../../components/dashboards/mobile/FilterSheets";
import useNCAAFRailFilters from "../hooks/useNCAAFRailFilters";
import MobileNCAAFOddsSheet from "../components/mobile/MobileNCAAFOddsSheet";
import MobileNCAAFPredictionSheet from "../components/mobile/MobileNCAAFPredictionSheet";
import { EmptyState } from "../../../../components/shared/loading/EmptyState";
import { LoadingSpinner } from "../../../../components/shared/loading/LoadingSpinner";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nflDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nfl-dash.pt-br";
import { NCAAF_HEADER_COPY } from "../constants/headerContent";
import { DatePickerSheet } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import {
  getDefaultFilters,
  getGames,
  getTeams,
  getStandings
} from "../services/ncaafDashboardService";

const DEFAULT_GAMES_PAGINATION = {
  currentPage: 1,
  perPage: 6,
  totalPages: 1
};

const MOBILE_TABS = [
  { id: 'games', label: 'Games'},
  { id: 'rankings', label: 'Rankings'},
  { id: 'teams', label: 'Teams'},
//  { id: 'athletes', label: 'Student Athletes'}
];

const NCAAFMobileDashboard = () => {
  const defaultFilters = useMemo(() => getDefaultFilters(), []);
  const defaultLeague = defaultFilters.league;
  const defaultSeason = defaultFilters.season;
  const defaultDate = defaultFilters.date;
  const defaultCountry = defaultFilters.country;
  const [activeTab, setActiveTab] = useState('games');
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gamesPagination, setGamesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [showOdds, setShowOdds] = useState(false);
  const [showPredictionSheet, setShowPredictionSheet] = useState(false);
  const [selectedGameForPrediction, setSelectedGameForPrediction] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const [sheetSelectionCount, setSheetSelectionCount] = useState(0);
  const cacheCheckKeyRef = useRef(null);
  const standingsRef = useRef([]);
  const teamsRef = useRef([]);

  const isBrazilian = getBrazilianStatus();
  const t = useCallback((text) => (isBrazilian ? nflDashPtBR[text] || text : text), [isBrazilian]);

  // Prediction hook
  const {
    isLoading: isPredicting,
    selectedGames,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    isGameFrozen,
    checkCachedPredictions
  } = useQuickPrediction();

  // Rail filters
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showConferenceSheet,
    setShowConferenceSheet,
    showTeamSheet,
    setShowTeamSheet
  } = useNCAAFRailFilters({
    onFiltersChange: () => setGamesPagination((prev) => ({ ...prev, currentPage: 1 }))
  });

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const refreshPagination = useCallback(
    (totalItems) => {
      setGamesPagination((prev) => {
        const totalPages = Math.max(1, Math.ceil(totalItems / prev.perPage));
        return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
      });
    },
    [setGamesPagination]
  );

  const loadGames = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const requestFilters = {
        league: defaultLeague,
        season: defaultSeason,
        date: ensureISODate(railFilters.date, defaultDate),
        country: defaultCountry,
        teamId: railFilters.team?.id || null,
        conference: railFilters.conference?.name || railFilters.conference?.id || railFilters.conference || "",
        division: railFilters.division || "all",
        status: railFilters.status || "all"
      };

      const gamesData = await getGames({
        ...requestFilters,
        standings: standingsRef.current,
        teams: teamsRef.current
      });

      const gamesWithIds = gamesData.map((game, index) => ({
        ...game,
        id: game.game?.id || game.id || `game-${index}`
      }));

      setGames(gamesWithIds);
      refreshPagination(gamesWithIds.length);
    } catch (err) {
      console.error("Failed to load NCAAF games:", err);
      setError(t("Failed to load games"));
      setGames([]);
      refreshPagination(0);
    } finally {
      setLoading(false);
    }
  }, [
    defaultCountry,
    defaultDate,
    defaultLeague,
    defaultSeason,
    railFilters.conference,
    railFilters.date,
    railFilters.division,
    railFilters.status,
    railFilters.team,
    refreshPagination,
    t
  ]);

  const loadTeams = useCallback(async () => {
    try {
      const teamsData = await getTeams({
        league: defaultLeague,
        season: defaultSeason
      });
      setTeams(teamsData);
      teamsRef.current = teamsData;
    } catch (err) {
      console.error("Failed to load teams:", err);
      setTeams([]);
    }
  }, [defaultLeague, defaultSeason]);

  const loadStandings = useCallback(async () => {
    try {
      const standingsData = await getStandings({
        league: defaultLeague,
        season: defaultSeason
      });
      setStandings(standingsData);
      standingsRef.current = standingsData;
    } catch (err) {
      console.error("Failed to load standings:", err);
      setStandings([]);
    }
  }, [defaultLeague, defaultSeason]);

  useEffect(() => {
    loadTeams();
    loadStandings();
  }, [loadTeams, loadStandings]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Prime predictions from cache/Firestore when games load
  useEffect(() => {
    if (activeTab !== 'games') return;
    if (!games.length) return;
    const gameIds = games.map((g) => g.game?.id || g.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;

    checkCachedPredictions?.("ncaaf", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NCAAF mobile):", err)
    );
  }, [activeTab, games, checkCachedPredictions]);

  const handleOpenOdds = useCallback((game) => {
    setSelectedGameForOdds(game);
    setShowOdds(true);
  }, []);

  const handleCloseOdds = useCallback(() => {
    setSelectedGameForOdds(null);
    setShowOdds(false);
  }, []);

  const handleOpenPredictionSheet = useCallback(
    (game) => {
      if (!game) return;
      const gamePrediction = getGamePrediction(game.id);
      setSelectedGameForPrediction(game);
      setSelectedPrediction(gamePrediction);
      setShowPredictionSheet(true);
      setSheetSelectionCount(selectionCount);
    },
    [getGamePrediction, selectionCount]
  );

  const handleClosePredictionSheet = useCallback(() => {
    setShowPredictionSheet(false);
    setSelectedGameForPrediction(null);
    setSelectedPrediction(null);
    setSheetSelectionCount(0);
  }, []);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("ncaaf");
    setPredictionFlowActive(false);
  }, [predictSelectedGames]);

  useEffect(() => {
    if (showPredictionSheet && selectedGameForPrediction) {
      const updatedPrediction = getGamePrediction(selectedGameForPrediction.id);
      setSelectedPrediction(updatedPrediction);
    }
  }, [showPredictionSheet, selectedGameForPrediction, getGamePrediction]);

  // Keep local sheet count in sync while the sheet is open
  useEffect(() => {
    if (showPredictionSheet) {
      setSheetSelectionCount(selectionCount);
    }
  }, [selectionCount, showPredictionSheet]);

  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
      setSheetSelectionCount(0);
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  const filteredHeaderCopy = useMemo(() => NCAAF_HEADER_COPY, []);

  return (
    <ErrorBoundary onReset={loadGames}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader title="NCAAF Dashboard" showBackButton />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...filteredHeaderCopy} />

          <div className="mt-4 mb-4">
            <RailNavigation
              sport="ncaaf"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          {/* Tab Navigation */}
          <TabNavigation
            tabs={MOBILE_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {error && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="text-red-300" size={18} />
              <p>{error}</p>
            </div>
          )}

          {/* Games Tab */}
          {activeTab === 'games' && (
            <MobileNCAAFGamesView
              games={games}
              loading={loading}
              pagination={gamesPagination}
              onPageChange={handleGamesPageChange}
              t={t}
              isGameSelected={isGameSelected}
              isGameFrozen={isGameFrozen}
              onToggleSelection={toggleGameSelection}
              getGamePrediction={getGamePrediction}
              onViewOdds={handleOpenOdds}
              onViewPrediction={handleOpenPredictionSheet}
            />
          )}

          {/* Rankings Tab */}
          {activeTab === 'rankings' && (
            <div className="space-y-4">
              {loading ? (
                <LoadingSpinner message="Loading rankings..." />
              ) : standings.length === 0 ? (
                <EmptyState
                  title="No rankings available"
                  description="Rankings data will be available soon"
                  icon={Trophy}
                />
              ) : (
                <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Trophy size={16} className="text-amber-400" />
                    Top 25 Rankings
                  </h3>
                  <div className="space-y-2">
                    {standings.slice(0, 25).map((team, idx) => (
                      <div
                        key={`top25-${team.team?.id || idx}`}
                        className="flex items-center justify-between py-2 border-b border-white/10 last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-6 ${idx < 5 ? 'text-amber-400' : 'text-white/60'}`}>
                            {team.position || idx + 1}
                          </span>
                          {team.team?.logo && (
                            <img src={team.team.logo} alt={team.team?.name} className="w-6 h-6 object-contain" />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-white block truncate">{team.team?.name || 'Unknown'}</span>
                            {team.conference && (
                              <span className="text-xs text-white/40 block truncate">{team.conference}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs flex-shrink-0">
                          <span className="text-white font-medium">{team.won || 0}-{team.lost || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div className="space-y-4">
              {loading ? (
                <LoadingSpinner message="Loading teams..." />
              ) : teams.length === 0 ? (
                <EmptyState
                  title="No teams available"
                  description="Teams data will be available soon"
                  icon={Users}
                />
              ) : (
                <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Users size={16} className="text-emerald-400" />
                    All Teams ({teams.length})
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {teams.map((team, idx) => (
                      <div
                        key={`team-${team.id || idx}`}
                        className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/5"
                      >
                        {team.logo && (
                          <img src={team.logo} alt={team.name} className="w-10 h-10 object-contain" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-white font-medium block truncate">{team.name || 'Unknown'}</span>
                          {team.conference && (
                            <span className="text-xs text-white/40 block truncate">{team.conference}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Student Athletes Tab */}
          {activeTab === 'athletes' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
                <div className="flex flex-col items-center text-center">
                  <div className="p-3 bg-amber-500/20 rounded-full mb-4">
                    <Users className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Coming Soon</h3>
                  <p className="text-sm text-white/60">
                    Student athlete data and statistics will be available in the next update
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date, defaultDate)}
          onDateSelect={(dateValue) => {
            const formatted = ensureISODate(dateValue, defaultDate);
            updateRailFilter("date", formatted);
            setShowDateSheet(false);
          }}
        />

        <ConferenceSheet
          isOpen={showConferenceSheet}
          onClose={() => setShowConferenceSheet(false)}
          selectedConference={railFilters.conference}
          onConferenceSelect={(conference) => {
            updateRailFilter("conference", conference);
            setShowConferenceSheet(false);
          }}
        />

        <TeamSheet
          isOpen={showTeamSheet}
          onClose={() => setShowTeamSheet(false)}
          selectedTeam={railFilters.team}
          onTeamSelect={(team) => {
            updateRailFilter("team", team);
            setShowTeamSheet(false);
          }}
          teams={teams}
          sport="ncaaf"
        />

        <MobileNCAAFOddsSheet isOpen={showOdds} game={selectedGameForOdds} onClose={handleCloseOdds} />

        <MobileNCAAFPredictionSheet
          isOpen={showPredictionSheet}
          game={selectedGameForPrediction}
          prediction={selectedPrediction}
          selectionCount={sheetSelectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          onPredict={handlePredictRequest}
          onClear={clearSelections}
          onClose={handleClosePredictionSheet}
          t={t}
        />
      </div>
    </ErrorBoundary>
  );
};

export default NCAAFMobileDashboard;
